import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { PopupNames } from 'core';
import { usePopupStateOperations } from 'core/hooks';
import { PATHS } from 'router/config';

import * as L from '../StudentDetail.styled';
import { paidPercent, progressTone, formatMoneyCurrency } from './format';
import { InvoicesBlock } from './InvoicesBlock';
import { MoveToGroupInlinePopup } from './MoveToGroupInlinePopup';
import { PaymentsBlock } from './PaymentsBlock';
import { RequisitesBlock } from './RequisitesBlock';
import { IAgreement, IStudent } from './types';

interface IAgreementCardProps {
  agreement: IAgreement;
  studentId: string;
  /** The carded student — `PaymentsBlock` needs it whole to build the
   *  `editStudentWithAgreement` payload for «+ Взнос в план» (the
   *  mutation requires the full student object, not a patch). */
  student: IStudent;
  /** Email of the carded student — used by `PaymentsBlock` to seed the
   *  «Привязать счёт» popup with email-auto-matched invoices. */
  studentEmail: string | null;
  /** Default expanded state — Overview opens active agreements,
   *  Agreements tab opens everything. */
  defaultExpanded?: boolean;
  /** Called after a popup mutation succeeds so the parent refetches. */
  onChanged?: () => void;
}

/**
 * The main «agreement file» — collapsible card with header (title, group,
 * PandaDoc link, money/progress) and an expanded body that stacks the
 * three subblocks: Payments, Invoices, Requisites, plus a row of agreement
 * actions (Edit, Open in Fakturownia).
 *
 * Reuses existing popups:
 *   - `EditAgreementPopup` for full edit (handles student + agreement + payments).
 *   - `LinkInvoicePaymentPopup` is dispatched from inside `InvoicesBlock` rows.
 */
export const AgreementCard = memo(
  ({
    agreement,
    studentId,
    student,
    studentEmail,
    defaultExpanded = true,
    onChanged,
  }: IAgreementCardProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();

    const [open, setOpen] = useState(defaultExpanded && !agreement.terminated);
    const [moving, setMoving] = useState(false);

    const onEdit = useCallback(() => {
      showPopup(PopupNames.editAgreement, {
        agreementId: agreement.id,
        studentId,
      });
    }, [showPopup, agreement.id, studentId]);

    const pct = paidPercent(agreement.budgetAmount, agreement.paidAmount);
    const tone = progressTone(pct);

    const pandadoc = agreement.pandadocDocument;
    const pandadocUrl = pandadoc
      ? `https://app.pandadoc.com/a/#/documents/${pandadoc.pandadocId}`
      : null;

    const groupRoute = agreement.group
      ? PATHS.groupDetail.replace(':id', agreement.group.id)
      : null;

    return (
      <L.AgreementCardWrap terminated={agreement.terminated}>
        <L.AgreementHeader open={open}>
          <L.AgreementToggle
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            <L.Chevron open={open}>▶</L.Chevron>
            <L.AgreementTitleLine>
              <span>{agreement.agreementTitle || '—'}</span>
              {agreement.terminated ? (
                <L.Tag tone="danger">
                  {t('pages.studentDetail.agreement.terminated')}
                </L.Tag>
              ) : (
                <L.Tag tone="ok">
                  {t('pages.studentDetail.agreement.active')}
                </L.Tag>
              )}
              {agreement.group?.course?.name ? (
                <L.AgreementMeta>
                  · {agreement.group.course.name}
                </L.AgreementMeta>
              ) : null}
              <L.AgreementMeta>· {agreement.currency}</L.AgreementMeta>
            </L.AgreementTitleLine>
          </L.AgreementToggle>
          <L.HeaderActions style={{ padding: '8px 16px 8px 0' }}>
            {groupRoute ? (
              <L.PrimaryActionButton
                type="button"
                onClick={() => history.push(groupRoute)}
              >
                {agreement.group?.name}&nbsp;↗
              </L.PrimaryActionButton>
            ) : null}
            {pandadocUrl ? (
              <L.PrimaryActionButton
                type="button"
                onClick={() => window.open(pandadocUrl, '_blank')}
              >
                {t('pages.studentDetail.agreement.openPandadoc')}&nbsp;↗
              </L.PrimaryActionButton>
            ) : null}
            {!agreement.terminated ? (
              <L.PrimaryActionButton
                type="button"
                onClick={() => setMoving(true)}
              >
                {t('pages.studentDetail.agreement.moveGroup')}
              </L.PrimaryActionButton>
            ) : null}
            <L.PrimaryActionButton
              tone="primary"
              type="button"
              onClick={onEdit}
            >
              {t('pages.studentDetail.agreement.edit')}
            </L.PrimaryActionButton>
          </L.HeaderActions>
        </L.AgreementHeader>

        {open ? (
          <L.AgreementBody>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div>
                <L.Muted>{t('pages.studentDetail.agreement.budget')}</L.Muted>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {formatMoneyCurrency(
                    agreement.budgetAmount,
                    agreement.currency,
                  )}
                </div>
              </div>
              <div>
                <L.Muted>{t('pages.studentDetail.agreement.paid')}</L.Muted>
                <div
                  style={{ fontSize: 16, fontWeight: 700, color: '#16a34a' }}
                >
                  {formatMoneyCurrency(
                    agreement.paidAmount,
                    agreement.currency,
                  )}
                </div>
              </div>
              <div>
                <L.Muted>{t('pages.studentDetail.agreement.debt')}</L.Muted>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: pct >= 100 ? '#16a34a' : '#d97706',
                  }}
                >
                  {formatMoneyCurrency(
                    Math.max(
                      0,
                      (agreement.budgetAmount || 0) -
                        (agreement.paidAmount || 0),
                    ),
                    agreement.currency,
                  )}
                </div>
              </div>
            </div>

            <L.ProgressTrack>
              <L.ProgressFill pct={pct} tone={tone} />
            </L.ProgressTrack>
            <L.Muted>{`${Math.round(pct)}% ${t(
              'pages.studentDetail.agreement.paidOf',
            )}`}</L.Muted>

            {agreement.date || agreement.lastLesson?.date ? (
              <div style={{ marginTop: 8 }}>
                <L.Muted>
                  {t('pages.studentDetail.agreement.period')}:&nbsp;
                </L.Muted>
                {agreement.date
                  ? moment(agreement.date).format('DD.MM.YYYY')
                  : '—'}
                {' – '}
                {agreement.lastLesson?.date
                  ? moment(agreement.lastLesson.date).format('DD.MM.YYYY')
                  : agreement.terminatedDate
                  ? moment(agreement.terminatedDate).format('DD.MM.YYYY')
                  : '—'}
              </div>
            ) : null}

            <PaymentsBlock
              agreement={agreement}
              student={student}
              studentId={studentId}
              studentEmail={studentEmail}
              onChanged={onChanged}
            />
            <InvoicesBlock agreement={agreement} onChanged={onChanged} />
            <RequisitesBlock agreement={agreement} />
          </L.AgreementBody>
        ) : null}

        {moving ? (
          <MoveToGroupInlinePopup
            agreement={agreement}
            studentId={studentId}
            onClose={() => setMoving(false)}
            onMoved={onChanged}
          />
        ) : null}
      </L.AgreementCardWrap>
    );
  },
);
